import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

import { Button } from "../ui/button";

// Aviso de versão nova.
//
// O registro está em `registerType: "prompt"`: o service worker baixa a versão
// nova sozinho, mas ela só passa a valer quando todas as abas fecham.
// Instalado como aplicativo, ninguém fecha — a pessoa fica semanas numa versão
// antiga sem saber, e "já corrigi isso" vira uma discussão sem fim.
//
// Aqui ela fica sabendo e recarrega quando quiser: no meio de uma conversa,
// recarregar sozinho seria pior do que a versão velha.

// De quanto em quanto tempo perguntar ao servidor se saiu versão nova. O
// navegador só reconsulta o sw.js numa navegação — e o painel fica aberto o
// dia inteiro sem nenhuma.
const INTERVALO_DE_CHECAGEM = 30 * 60 * 1000;

const AtualizacaoDisponivel = () => {
	const [pronta, setPronta] = useState(false);
	const [atualizar, setAtualizar] = useState(null);

	useEffect(() => {
		let cancelado = false;
		let timer = null;

		// Import dinâmico: o módulo virtual só existe quando o PWA está ativo
		// (não existe em `npm run dev` sem a opção `devOptions`), e um import
		// estático quebraria o desenvolvimento.
		import("virtual:pwa-register")
			.then(({ registerSW }) => {
				if (cancelado) return;

				const atualizarSW = registerSW({
					onNeedRefresh() {
						setPronta(true);
					},
					onRegisteredSW(url, registro) {
						if (!registro) return;

						// Só interessa o service worker que ESTE registro instalou.
						// Um worker antigo ou de outra origem no mesmo escopo também
						// aparece em `registro.waiting`, e ele nem entende o
						// SKIP_WAITING que o botão envia: o aviso apareceria para
						// algo que o clique não resolve.
						const nossoSW = new URL(url, window.location.href).href;

						// Quem abriu o app DEPOIS do deploy não via aviso nenhum.
						//
						// `onNeedRefresh` nasce de um evento: a versão nova saindo de
						// "instalando" para "esperando" com a aba aberta. Quem chegou
						// depois encontra a versão nova JÁ esperando — evento nenhum
						// para escutar, e o app fica na versão velha sem nada na tela
						// dizendo o porquê. Era o pior dos mundos: não atualiza
						// sozinho (de propósito) e não avisa (sem querer).
						//
						// Perguntar direto ao registro cobre esse caso e também a aba
						// que já estava aberta e cuja checagem periódica encontra a
						// versão pronta.
						//
						// Vale a partir do deploy SEGUINTE ao que trouxe este código:
						// quem já está preso num service worker antigo não roda nada
						// disto, e sai de lá fechando todas as abas do app uma vez.
						// Forçar a ativação resolveria de imediato e recarregaria a
						// tela no meio de um atendimento — que é justamente o que
						// este desenho evita.
						const conferirEspera = () => {
							if (registro.waiting?.scriptURL === nossoSW) setPronta(true);
						};

						conferirEspera();

						timer = setInterval(() => {
							// Offline o `update()` rejeita; sem o catch vira
							// "unhandled rejection" no console a cada 30 min.
							registro
								.update()
								.then(conferirEspera)
								.catch(() => {});
						}, INTERVALO_DE_CHECAGEM);
					},
				});

				setAtualizar(() => atualizarSW);
			})
			.catch(() => {
				// Sem service worker (desenvolvimento, navegador antigo) não há
				// atualização a avisar. Não é erro.
			});

		return () => {
			cancelado = true;
			if (timer) clearInterval(timer);
		};
	}, []);

	// O `updateServiceWorker` do vite-plugin-pwa só manda SKIP_WAITING; quem
	// recarrega a página é um listener de `controlling` que a biblioteca
	// registra no caminho do evento `waiting`. No caminho novo — aviso vindo de
	// `registro.waiting`, sem evento — esse listener não existe: o worker novo
	// assumiria, a página continuaria rodando o código velho e o aviso ficaria
	// preso na tela. Aviso que não some depois de resolvido ensina a ignorar
	// aviso.
	const recarregar = () => {
		if (!atualizar) return;

		// O reload vem do efeito de `controllerchange` acima, que vale para
		// qualquer origem da troca — inclusive este clique.
		atualizar(true);
	};

	if (!pronta) return null;

	return (
		<div
			role="status"
			className="fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-50 flex items-center gap-3 rounded-xl border bg-card p-3 shadow-lg md:inset-x-auto md:right-4 md:bottom-4 md:max-w-sm"
		>
			<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
				<RefreshCw className="h-4 w-4" />
			</div>
			<div className="min-w-0 flex-1">
				<p className="text-sm font-medium">Nova versão disponível</p>
				<p className="text-xs text-muted-foreground">
					Recarregue quando puder. Nada do que está aberto se perde.
				</p>
			</div>
			<Button
				size="sm"
				className="h-8 shrink-0"
				onClick={recarregar}
			>
				Recarregar
			</Button>
		</div>
	);
};

export default AtualizacaoDisponivel;
